/**
 * 🔐 SECURE LOGGER - LGPD & OWASP Compliant
 * Prevents sensitive data leaks in production logs
 *
 * SECURITY FEATURES:
 * - Sanitizes sensitive data before logging
 * - Removes PII and financial information
 * - Environment-aware logging levels
 * - Structured logging with context
 * - Error tracking without exposing internals
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  userId?: string;
  sessionId?: string;
  action?: string;
  component?: string;
  [key: string]: any;
}

interface SanitizedError {
  name: string;
  message: string;
  code?: string;
  timestamp: string;
  level: string;
}

class SecureLogger {
  private isProduction: boolean;
  private logLevel: LogLevel;

  // Sensitive patterns to sanitize
  private readonly SENSITIVE_PATTERNS = [
    /\b[\w\.-]+@[\w\.-]+\.\w+\b/g, // Email addresses
    /\b\d{11}\b/g, // CPF (11 digits)
    /\b\d{14}\b/g, // CNPJ (14 digits)
    /\b\d{4}[\s\-]?\d{4}[\s\-]?\d{4}[\s\-]?\d{4}\b/g, // Credit card numbers
    /\b\(\d{2}\)\s?\d{4,5}[\-\s]?\d{4}\b/g, // Phone numbers BR format
    /\bsk-[a-zA-Z0-9]{48,}\b/g, // API keys (sk- prefix)
    /\bBearer\s+[a-zA-Z0-9\-._~+/]+\b/g, // JWT tokens
    /\b[A-Za-z0-9+/]{20,}={0,2}\b/g, // Base64 tokens
    /password|senha|secret|token|key|credential/i, // Sensitive field names
  ];

  // Safe fields that can be logged
  private readonly SAFE_FIELDS = [
    'id',
    'userId',
    'sessionId',
    'timestamp',
    'action',
    'component',
    'status',
    'method',
    'path',
    'duration',
    'level',
    'message',
  ];

  constructor() {
    this.isProduction = import.meta.env.PROD || process.env.NODE_ENV === 'production';
    this.logLevel = this.isProduction ? 'warn' : 'debug';
  }

  /**
   * Sanitize sensitive data from any object or string
   */
  private sanitize(data: any): any {
    if (typeof data === 'string') {
      return this.sanitizeString(data);
    }

    if (data instanceof Error) {
      return this.sanitizeError(data);
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitize(item));
    }

    if (typeof data === 'object' && data !== null) {
      return this.sanitizeObject(data);
    }

    return data;
  }

  private sanitizeString(str: string): string {
    let sanitized = str;

    // Replace sensitive patterns with masked values
    this.SENSITIVE_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    });

    return sanitized;
  }

  private sanitizeError(error: Error): SanitizedError {
    const sanitizedMessage = this.sanitizeString(error.message);

    return {
      name: error.name,
      message: this.isProduction ? 'An error occurred' : sanitizedMessage,
      code: (error as any).code || 'UNKNOWN_ERROR',
      timestamp: new Date().toISOString(),
      level: 'error',
    };
  }

  private sanitizeObject(obj: any): any {
    const sanitized: any = {};

    Object.keys(obj).forEach(key => {
      const lowerKey = key.toLowerCase();

      // Skip sensitive fields entirely
      if (this.SENSITIVE_PATTERNS.some(pattern => lowerKey.match(pattern))) {
        sanitized[key] = '[REDACTED]';
        return;
      }

      // Recursively sanitize nested objects
      sanitized[key] = this.sanitize(obj[key]);
    });

    return sanitized;
  }

  /**
   * Create structured log entry
   */
  private createLogEntry(level: LogLevel, message: string, context?: LogContext): any {
    const timestamp = new Date().toISOString();

    const logEntry = {
      timestamp,
      level,
      message: this.sanitizeString(message),
      ...this.sanitize(context || {}),
      environment: this.isProduction ? 'production' : 'development',
    };

    // In production, only include safe fields
    if (this.isProduction) {
      const safeEntry: any = {};
      this.SAFE_FIELDS.forEach(field => {
        if (logEntry[field] !== undefined) {
          safeEntry[field] = logEntry[field];
        }
      });
      return safeEntry;
    }

    return logEntry;
  }

  /**
   * Check if log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };

    return levels[level] >= levels[this.logLevel];
  }

  /**
   * Debug logging (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog('debug')) return;

    const logEntry = this.createLogEntry('debug', message, context);
    console.debug('🐛', JSON.stringify(logEntry, null, 2));
  }

  /**
   * Info logging
   */
  info(message: string, context?: LogContext): void {
    if (!this.shouldLog('info')) return;

    const logEntry = this.createLogEntry('info', message, context);
    console.info('ℹ️', JSON.stringify(logEntry));
  }

  /**
   * Warning logging
   */
  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog('warn')) return;

    const logEntry = this.createLogEntry('warn', message, context);
    console.warn('⚠️', JSON.stringify(logEntry));
  }

  /**
   * Error logging - always outputs in production
   */
  error(message: string, error?: Error, context?: LogContext): void {
    const sanitizedError = error ? this.sanitizeError(error) : undefined;

    const logEntry = this.createLogEntry('error', message, {
      ...context,
      error: sanitizedError,
    });

    console.error('🚨', JSON.stringify(logEntry));

    // In production, also send to monitoring service
    if (this.isProduction && error) {
      this.sendToMonitoring(logEntry);
    }
  }

  /**
   * Log authentication events (security-sensitive)
   */
  security(message: string, context?: LogContext): void {
    const securityEntry = this.createLogEntry('warn', `[SECURITY] ${message}`, {
      ...context,
      security: true,
    });

    console.warn('🔐', JSON.stringify(securityEntry));

    if (this.isProduction) {
      this.sendToSecurityLog(securityEntry);
    }
  }

  /**
   * Log LGPD/privacy events
   */
  privacy(message: string, context?: LogContext): void {
    const privacyEntry = this.createLogEntry('info', `[LGPD] ${message}`, {
      ...context,
      privacy: true,
    });

    console.info('🛡️', JSON.stringify(privacyEntry));
  }

  /**
   * Send critical errors to monitoring service
   */
  private sendToMonitoring(logEntry: any): void {
    try {
      // In a real app, integrate with services like:
      // - Sentry
      // - Datadog
      // - CloudWatch
      // - Azure Monitor

      // For now, just ensure it's logged securely
      fetch('/api/monitoring/error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logEntry),
      }).catch(() => {
        // Fail silently to avoid recursive logging
      });
    } catch {
      // Fail silently
    }
  }

  /**
   * Send security events to dedicated security log
   */
  private sendToSecurityLog(logEntry: any): void {
    try {
      fetch('/api/security/audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logEntry),
      }).catch(() => {
        // Fail silently
      });
    } catch {
      // Fail silently
    }
  }

  /**
   * Legacy console.* replacement methods
   */
  log = this.info.bind(this);

  // Legacy method for gradual migration
  migrate(originalMessage: string, error?: any, context?: LogContext): void {
    this.warn('Console.* usage detected - migrate to secure logger', {
      originalMessage: this.sanitizeString(originalMessage),
      hasError: !!error,
      ...context,
    });
  }
}

// Export singleton instance
export const secureLogger = new SecureLogger();

// Legacy console replacement (for migration phase)
export const logger = secureLogger;

// Development helper functions
export const logSafe = secureLogger.info.bind(secureLogger);
export const logError = secureLogger.error.bind(secureLogger);
export const logWarn = secureLogger.warn.bind(secureLogger);
export const logDebug = secureLogger.debug.bind(secureLogger);

export default secureLogger;

/**
 * USAGE EXAMPLES:
 *
 * // ✅ SECURE - Good practices
 * secureLogger.info('User logged in', { userId: user.id, action: 'login' });
 * secureLogger.error('Database connection failed', error, { component: 'auth' });
 * secureLogger.privacy('Consent updated', { userId: '123', consent: 'marketing' });
 *
 * // ❌ INSECURE - Will be sanitized
 * console.error('Auth error:', { email: 'user@test.com', password: '123' });
 * // Becomes: secureLogger.error('Auth error', error, { email: '[REDACTED]' });
 *
 * // 🔧 MIGRATION - Temporary helper
 * secureLogger.migrate('Old console.log message', error);
 */
